import * as SubframeCore from '@subframe/core';
import { AccountStats, Cluster, ClusterStatus } from 'api/models';
import { SetStateFunction } from 'models/react';
import { SyntheticEvent, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  IconButton,
  Select,
  SkeletonText,
  Tooltip,
} from 'subframe/index';
import { Searchbar } from 'components/design-system/Searchbar';
import { extractMajorMinor } from 'utils/extractMajorMinor';
import {
  ClusterFilters,
  ClustersViewLayout,
  ClustersViewLayoutValues,
} from '../models';
import RiskLedgerClustersViews from './RiskLedgerClustersViews';
import { useDebounce } from 'react-use';
import AnalyticsEventLogger from 'utils/AnalyticsEventLogger';
import { truncateAtLength } from 'utils/formatString';

interface RiskLedgerClustersDataProps {
  clusters: Cluster[];
  totalClusters: Cluster[];
  isLoadingClusters: boolean;
  showExampleData: boolean | undefined;
  setFilters: SetStateFunction<ClusterFilters | undefined>;
  filters: ClusterFilters | undefined;
  // TODO: fix this to make clusterStatus part of filters, when api supports it instead of separate query parameter
  clustersStatus: ClusterStatus[];
  setClusterStatus: SetStateFunction<ClusterStatus[]>;
  handleOnChange: (
    event: SyntheticEvent<Element, Event>,
    newValue: string,
  ) => void;
  mutateClusters: () => void;
  clusterViewLayout: ClustersViewLayoutValues;
  handleAlignment: (newAlignment: ClustersViewLayoutValues) => void;
  isTotalClustersLoading: boolean;
  clustersToShowFilterLoading: boolean;
  noClustersFound: boolean;
  noMatchingClustersFound: boolean;
}

function RiskLedgerActionHeader({
  clusters,
  totalClusters,
  isLoadingClusters,
  showExampleData,
  setFilters,
  filters,
  handleOnChange,
  mutateClusters,
  clusterViewLayout,
  handleAlignment,
  isTotalClustersLoading,
  clustersToShowFilterLoading,
  noClustersFound,
  noMatchingClustersFound,
  clustersStatus,
  setClusterStatus,
}: RiskLedgerClustersDataProps) {
  const [clusterFilters, setClusterFilters] = useState<ClusterFilters>({
    environments: filters?.environments || ([] as string[]),
    versions: filters?.versions || ([] as string[]),
    providers: filters?.providers || ([] as string[]),
  });
  const { logEvent } = AnalyticsEventLogger();

  useDebounce(
    () => {
      if (clusterFilters) {
        logEvent('filter-selected', filters);
        onFiltersChange();
      }
    },
    1000,
    [clusterFilters],
  );

  const onFiltersChange = () => {
    if (
      !!clusterFilters.environments ||
      !!clusterFilters.versions ||
      !!clusterFilters.providers
    ) {
      const filter = {
        environments: [] as string[],
        versions: [] as string[],
        providers: [] as string[],
      };

      // Only add environments if it's not empty
      if (
        clusterFilters?.environments &&
        clusterFilters?.environments.length > 0
      ) {
        filter.environments = clusterFilters?.environments;
      }

      // Only add versions if it's not empty
      if (clusterFilters?.versions && clusterFilters?.versions.length > 0) {
        filter.versions = clusterFilters?.versions;
      }

      // Only add providers if it's not empty
      if (clusterFilters?.providers && clusterFilters?.providers.length > 0) {
        filter.providers = clusterFilters?.providers;
      }

      // Call the setFilters function with the filters object
      setFilters(filter);
    }
  };

  const { uniqueEnvironments, uniqueVersions, uniqueProviders } =
    useMemo(() => {
      const environments = new Set<string>();
      const versions = new Set<string>();
      const providers = new Set<string>();

      // Extract unique values from clusters
      totalClusters.forEach((cluster) => {
        // Add environment (case insensitive)
        if (cluster.environment) {
          environments.add(cluster.environment.toLowerCase());
        }

        // Add version (major.minor format)
        const version = extractMajorMinor(cluster.version || '');
        if (version) {
          versions.add(version);
        }

        // Add cloud provider
        if (cluster.cloud_provider) {
          providers.add(cluster.cloud_provider);
        }
      });

      return {
        uniqueEnvironments: Array.from(environments).sort((a, b) =>
          a.localeCompare(b),
        ),
        uniqueVersions: Array.from(versions).sort((a, b) => a.localeCompare(b)),
        uniqueProviders: Array.from(providers).sort((a, b) =>
          a.localeCompare(b),
        ),
      };
    }, [totalClusters]);

  const clusterStatusOptions = [
    { value: ClusterStatus.active, label: 'Active' },
    { value: ClusterStatus.deactivated, label: 'Deactivated' },
  ];

  const isDropdownDisabled =
    isLoadingClusters ||
    showExampleData ||
    (clustersStatus.length === 1 &&
      clustersStatus[0] === ClusterStatus.deactivated);
  return (
    <>
      {(!noClustersFound || noMatchingClustersFound) && (
        <div className="flex w-full flex-col items-start gap-4">
          <div className="flex w-full items-center justify-end gap-2">
            {/* TODO: replace with Subframe Searchbar */}
            <Searchbar
              onChange={handleOnChange}
              view={showExampleData ? 'example' : 'real'}
            />
            {isLoadingClusters ? (
              <>
                <SkeletonText className="h-10 w-32 flex-none" />
                <SkeletonText className="h-full w-32 flex-none" />
                <SkeletonText className="h-full w-32 flex-none" />
                <SkeletonText className="h-full w-32 flex-none" />
              </>
            ) : (
              <>
                <Select
                  className="h-auto w-44 flex-none"
                  icon={clustersStatus.length > 0 ? 'FeatherFilter' : null}
                  placeholder={truncateAtLength(
                    clustersStatus.length > 0
                      ? clustersStatus
                          .map(
                            (status) =>
                              clusterStatusOptions.find(
                                (option) => option.value === status,
                              )?.label || status,
                          )
                          .join(', ')
                      : 'Status',
                    16,
                  )}
                  variant={clustersStatus.length > 0 ? 'filled' : 'outline'}
                >
                  <div className="flex w-full items-center gap-2 pt-1 pr-1 pb-1 pl-3">
                    <CheckboxGroup label="Filter by Status" helpText="">
                      {clusterStatusOptions.map((option) => (
                        <Checkbox
                          checked={clustersStatus.includes(option.value)}
                          key={option.value}
                          label={option.label}
                          value={option.value}
                          onClick={() => {
                            if (clustersStatus.includes(option.value)) {
                              setClusterStatus(
                                clustersStatus.filter(
                                  (status) => status !== option.value,
                                ),
                              );
                            } else {
                              setClusterStatus([
                                ...clustersStatus,
                                option.value,
                              ]);
                            }
                          }}
                        />
                      ))}
                    </CheckboxGroup>
                  </div>
                  <div className="flex w-full flex-col items-start pt-0.5 pb-0.5">
                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-300" />
                  </div>
                  <Button
                    className="h-8 w-full flex-none justify-start"
                    variant="neutral-tertiary"
                    onClick={(event) => {
                      event.preventDefault();
                      setClusterStatus([]);
                      mutateClusters && mutateClusters();
                    }}
                  >
                    Clear Selection
                  </Button>
                </Select>
                <Select
                  className={`h-auto w-44 flex-none${
                    isDropdownDisabled ? ' opacity-50' : ''
                  }`}
                  icon={
                    filters?.environments?.length || 0 ? 'FeatherFilter' : null
                  }
                  variant={
                    (filters?.environments?.length || 0) > 0
                      ? 'filled'
                      : 'outline'
                  }
                  disabled={isDropdownDisabled}
                  placeholder={truncateAtLength(
                    filters?.environments?.join(',')
                      ? clusterFilters?.environments
                          ?.filter((env) => (env === '' ? 'default' : env))
                          .join(', ') || 'Environments'
                      : 'Environments',
                    16,
                  )}
                >
                  <div className="flex w-full items-center gap-2 pt-1 pr-1 pb-1 pl-3">
                    <CheckboxGroup label=" Filter by Environment" helpText="">
                      {uniqueEnvironments.map((environment) => (
                        <Checkbox
                          checked={clusterFilters.environments?.includes(
                            environment,
                          )}
                          key={environment}
                          label={environment}
                          value={environment}
                          onClick={() => {
                            const environments = filters?.environments || [];
                            const { environments: e, ...rest } = clusterFilters;
                            if (environments?.includes(environment)) {
                              setClusterFilters({
                                environments:
                                  clusterFilters?.environments?.filter(
                                    (env) => env !== environment,
                                  ),
                                ...rest,
                              });
                            } else {
                              const { environments: e, ...rest } =
                                clusterFilters;
                              setClusterFilters({
                                environments: e
                                  ? [...e, environment]
                                  : [environment],
                                ...rest,
                              });
                            }
                          }}
                        />
                      ))}
                    </CheckboxGroup>
                  </div>
                  <div className="flex w-full flex-col items-start pt-0.5 pb-0.5">
                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-300" />
                  </div>
                  <Button
                    className="h-8 w-full flex-none justify-start"
                    variant="neutral-tertiary"
                    onClick={(event) => {
                      event.preventDefault();
                      const { environments, ...rest } = filters || {};
                      setClusterFilters({
                        environments: [],
                        ...rest,
                      });

                      mutateClusters && mutateClusters();
                    }}
                  >
                    Clear Selection
                  </Button>
                </Select>

                <Select
                  className={`h-auto w-44 flex-none${
                    isDropdownDisabled ? ' opacity-50' : ''
                  }`}
                  icon={
                    clusterFilters?.versions?.length || 0
                      ? 'FeatherFilter'
                      : null
                  }
                  variant={
                    (clusterFilters?.versions?.length || 0) > 0
                      ? 'filled'
                      : 'outline'
                  }
                  disabled={isDropdownDisabled}
                  placeholder={truncateAtLength(
                    clusterFilters?.versions?.join(',') || 'Versions',
                    16,
                  )}
                >
                  <div className="flex w-full items-center gap-2 pt-1 pr-1 pb-1 pl-3">
                    <CheckboxGroup label="Filter by Version" helpText="">
                      {uniqueVersions.map((version) => (
                        <Checkbox
                          className="mx-1"
                          checked={clusterFilters?.versions?.includes(version)}
                          key={version}
                          label={version}
                          value={version}
                          onClick={() => {
                            const versions = filters?.versions || [];
                            const { versions: v, ...rest } = clusterFilters;
                            if (versions?.includes(version)) {
                              setClusterFilters({
                                versions: clusterFilters?.versions?.filter(
                                  (ver) => ver !== version,
                                ),
                                ...rest,
                              });
                            } else {
                              const { versions: v, ...rest } = clusterFilters;
                              setClusterFilters({
                                versions: v ? [...v, version] : [version],
                                ...rest,
                              });
                            }
                          }}
                        />
                      ))}
                    </CheckboxGroup>
                  </div>
                  <div className="flex w-full flex-col items-start pt-0.5 pb-0.5">
                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-300" />
                  </div>
                  <Button
                    className="h-8 w-full flex-none justify-start"
                    variant="neutral-tertiary"
                    onClick={(event) => {
                      event.preventDefault();
                      const { versions, ...rest } = filters || {};
                      setClusterFilters({
                        versions: [],
                        ...rest,
                      });

                      mutateClusters && mutateClusters();
                    }}
                  >
                    Clear Selection
                  </Button>
                </Select>

                <Select
                  className={`h-auto w-44 flex-none${
                    isDropdownDisabled ? ' opacity-50' : ''
                  }`}
                  icon={
                    clusterFilters?.providers?.length || 0
                      ? 'FeatherFilter'
                      : null
                  }
                  variant={
                    (clusterFilters?.providers?.length || 0) > 0
                      ? 'filled'
                      : 'outline'
                  }
                  disabled={isDropdownDisabled}
                  placeholder={truncateAtLength(
                    clusterFilters?.providers?.join(',') || 'Clouds',
                    16,
                  )}
                >
                  <div className="flex w-full items-center gap-2 pt-1 pr-1 pb-1 pl-3">
                    <CheckboxGroup label="Filter by Cloud Provider" helpText="">
                      {uniqueProviders.map((provider) => (
                        <Checkbox
                          checked={clusterFilters?.providers?.includes(
                            provider,
                          )}
                          key={provider}
                          label={provider}
                          value={provider}
                          onClick={() => {
                            const providers = filters?.providers || [];
                            const { providers: p, ...rest } = clusterFilters;
                            if (providers?.includes(provider)) {
                              setClusterFilters({
                                providers: clusterFilters?.providers?.filter(
                                  (pro) => pro !== provider,
                                ),
                                ...rest,
                              });
                            } else {
                              const { providers: p, ...rest } = clusterFilters;
                              setClusterFilters({
                                providers: p ? [...p, provider] : [provider],
                                ...rest,
                              });
                            }
                          }}
                        />
                      ))}
                    </CheckboxGroup>
                  </div>
                  <div className="flex w-full flex-col items-start pt-0.5 pb-0.5">
                    <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-300" />
                  </div>
                  <Button
                    className="h-8 w-full flex-none justify-start"
                    variant="neutral-tertiary"
                    onClick={(event) => {
                      event.preventDefault();
                      const { providers, ...rest } = filters || {};
                      setClusterFilters({
                        providers: [],
                        ...rest,
                      });

                      mutateClusters && mutateClusters();
                    }}
                  >
                    Clear Selection
                  </Button>
                </Select>
              </>
            )}
            <SubframeCore.Tooltip.Provider>
              <SubframeCore.Tooltip.Root>
                <SubframeCore.Tooltip.Trigger asChild={true}>
                  <div className="flex items-center justify-end gap-2 rounded border border-solid border-neutral-border pt-1 pr-1 pb-1 pl-1">
                    <SubframeCore.Tooltip.Provider>
                      <SubframeCore.Tooltip.Root>
                        <SubframeCore.Tooltip.Trigger asChild={true}>
                          <IconButton
                            icon="FeatherLayoutGrid"
                            variant={
                              clusterViewLayout === ClustersViewLayout.GRID
                                ? 'neutral-primary'
                                : undefined
                            }
                            onClick={() =>
                              handleAlignment(ClustersViewLayout.GRID)
                            }
                          />
                        </SubframeCore.Tooltip.Trigger>
                        <SubframeCore.Tooltip.Portal>
                          <SubframeCore.Tooltip.Content
                            side="bottom"
                            align="center"
                            sideOffset={4}
                            asChild={true}
                          >
                            <Tooltip>Grid View</Tooltip>
                          </SubframeCore.Tooltip.Content>
                        </SubframeCore.Tooltip.Portal>
                      </SubframeCore.Tooltip.Root>
                    </SubframeCore.Tooltip.Provider>
                    <SubframeCore.Tooltip.Provider>
                      <SubframeCore.Tooltip.Root>
                        <SubframeCore.Tooltip.Trigger asChild={true}>
                          <IconButton
                            variant={
                              clusterViewLayout === ClustersViewLayout.LIST
                                ? 'neutral-primary'
                                : undefined
                            }
                            onClick={() =>
                              handleAlignment(ClustersViewLayout.LIST)
                            }
                            icon="FeatherList"
                          />
                        </SubframeCore.Tooltip.Trigger>
                        <SubframeCore.Tooltip.Portal>
                          <SubframeCore.Tooltip.Content
                            side="bottom"
                            align="center"
                            sideOffset={4}
                            asChild={true}
                          >
                            <Tooltip>List View</Tooltip>
                          </SubframeCore.Tooltip.Content>
                        </SubframeCore.Tooltip.Portal>
                      </SubframeCore.Tooltip.Root>
                    </SubframeCore.Tooltip.Provider>
                  </div>
                </SubframeCore.Tooltip.Trigger>
              </SubframeCore.Tooltip.Root>
            </SubframeCore.Tooltip.Provider>
          </div>
          <div
            className={`flex h-full w-full max-w-${
              clusterViewLayout === ClustersViewLayout.GRID
                ? '[1200px]'
                : 'full'
            } grow shrink-0 basis-0 flex-col items-start gap-2`}
            style={{
              maxWidth:
                clusterViewLayout === ClustersViewLayout.GRID
                  ? '1200px'
                  : '100%',
            }}
          >
            <RiskLedgerClustersViews
              clusters={clusters}
              isLoadingClusters={isLoadingClusters}
              showExampleData={showExampleData}
              mutateClusters={mutateClusters}
              handleAlignment={handleAlignment}
              clusterViewLayout={clusterViewLayout}
              isTotalClustersLoading={isTotalClustersLoading}
              clustersToShowFilterLoading={clustersToShowFilterLoading}
              filters={filters}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default RiskLedgerActionHeader;
